/**
 * Ref-counted merge-poll pause reasons for Live Maneuvers.
 * Merge timer runs only when initial load is done and all pause counts are zero.
 */

export type LivePollPauseReason = 'interaction' | 'table-fetch' | 'view-load' | 'follow';

export type LivePollCoordinatorState = {
  pauseCounts: Record<LivePollPauseReason, number>;
  initialLoadDone: boolean;
  timerRunning: boolean;
};

export function createLivePollCoordinator(options: {
  onStartTimer: () => void;
  onStopTimer: () => void;
  logDebug?: (msg: string, data?: Record<string, unknown>) => void;
  /** Called by `releaseAllPauses` with whatever was being held, so a leak identifies itself. */
  onForceRelease?: (info: { held: Record<LivePollPauseReason, number>; heldTotal: number }) => void;
}) {
  const pauseCounts: Record<LivePollPauseReason, number> = {
    interaction: 0,
    'table-fetch': 0,
    'view-load': 0,
    follow: 0,
  };

  let initialLoadDone = false;
  let timerRunning = false;

  const totalPauseCount = (): number =>
    pauseCounts.interaction + pauseCounts['table-fetch'] + pauseCounts['view-load'] + pauseCounts.follow;

  const isPaused = (): boolean => totalPauseCount() > 0;

  const maybeStartTimer = (): void => {
    if (!initialLoadDone || isPaused()) {
      return;
    }
    if (!timerRunning) {
      timerRunning = true;
      options.onStartTimer();
      options.logDebug?.('LiveManeuvers: merge poll timer started');
    }
  };

  const stopTimerIfRunning = (): void => {
    if (timerRunning) {
      timerRunning = false;
      options.onStopTimer();
      options.logDebug?.('LiveManeuvers: merge poll timer stopped');
    }
  };

  const pause = (reason: LivePollPauseReason): void => {
    pauseCounts[reason] += 1;
    stopTimerIfRunning();
    options.logDebug?.('LiveManeuvers: merge polls paused', { reason, count: pauseCounts[reason] });
  };

  const resume = (reason: LivePollPauseReason): void => {
    if (pauseCounts[reason] <= 0) {
      return;
    }
    pauseCounts[reason] -= 1;
    options.logDebug?.('LiveManeuvers: merge poll pause released', {
      reason,
      remaining: pauseCounts[reason],
      totalPaused: totalPauseCount(),
    });
    maybeStartTimer();
  };

  const markInitialLoadDone = (): void => {
    initialLoadDone = true;
    maybeStartTimer();
  };

  /**
   * Force-clear every outstanding pause and re-plant the timer. The escape hatch for a LEAKED pause.
   *
   * Each pause reason has a release path that must run; one missed edge holds the merge timer for the
   * rest of the session, with Follow still reading LIVE. `liveStallWatchdog` decides WHEN this should
   * happen; this is what it calls.
   *
   * Also re-plants the timer when the handle has drifted from `timerRunning` — a stop that never
   * landed leaves the flag saying "running" over nothing, which `maybeStartTimer` would then refuse
   * to fix because it believes a timer already exists.
   *
   * Reports what it found through `onForceRelease` so the next leak names itself rather than being
   * silently papered over.
   */
  const releaseAllPauses = (): void => {
    const held = { ...pauseCounts };
    const heldTotal = totalPauseCount();
    pauseCounts.interaction = 0;
    pauseCounts['table-fetch'] = 0;
    pauseCounts['view-load'] = 0;
    pauseCounts.follow = 0;
    // Clear the flag before restarting: `maybeStartTimer` no-ops while it reads true, which is
    // exactly the drift case this exists to repair.
    timerRunning = false;
    options.onForceRelease?.({ held, heldTotal });
    maybeStartTimer();
  };

  const reset = (): void => {
    pauseCounts.interaction = 0;
    pauseCounts['table-fetch'] = 0;
    pauseCounts['view-load'] = 0;
    pauseCounts.follow = 0;
    initialLoadDone = false;
    stopTimerIfRunning();
  };

  const getState = (): LivePollCoordinatorState => ({
    pauseCounts: { ...pauseCounts },
    initialLoadDone,
    timerRunning,
  });

  return {
    pause,
    resume,
    releaseAllPauses,
    maybeStartTimer,
    isPaused,
    markInitialLoadDone,
    reset,
    getState,
    setInitialLoadDone: (value: boolean) => {
      initialLoadDone = value;
    },
  };
}

export type LivePollCoordinator = ReturnType<typeof createLivePollCoordinator>;
